import { useEffect, useState } from 'react'

import { getOpportunityDetail } from '../api'
import { toast } from '@sales-center/components'
import { useAppDispatch } from '@user/store/hooks/useAppDispatch'
import { useNavigate } from 'react-router-dom'

export const useGetOpportunityDetailOnMount = (opportunityId?: number) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const [status, setStatus] = useState<'pending' | 'success' | 'fail'>(
    'pending',
  )

  useEffect(() => {
    let mounted = true

    if (!opportunityId) {
      return () => {
        mounted = false
      }
    }

    // WWM-18150 - reset to 'pending' on every opportunityId change, not just the first mount.
    // EntityDetails only renders page content (and thus the Add Service drawer, etc.) once
    // status === 'success'. Without this reset, switching to a different opportunity in place
    // (same route, new id param) left status at 'success' from the PREVIOUS opportunity for the
    // entire duration of the new fetch, so the old, stale detail (including its serviceLocations)
    // stayed fully rendered and interactive instead of being replaced by the loading screen.
    setStatus('pending')

    const execute = async () => {
      try {
        const result = await dispatch(getOpportunityDetail(opportunityId))

        if (mounted) {
          if (getOpportunityDetail.fulfilled.match(result)) {
            setStatus('success')
          } else {
            if (result.payload?.Message === 'Permission denied') {
              navigate('/access-denied')
            } else {
              setStatus('fail')
              toast.error(
                result.payload?.Message || 'Error loading opportunity details.',
              )
            }
          }
        }
      } catch {
        if (mounted) {
          setStatus('fail')
        }
      }
    }
    execute()

    return () => {
      mounted = false
    }
  }, [dispatch, opportunityId, navigate])

  return status
}
